'use client';

import {
  useEffect,
  useState
} from 'react';

import
  SectionLayout
from '@/app/ui/section-layout';

import
  PrimaryButton
from '@repo/ui/primary-button';

import {
  getCampaignByKey
} from '@repo/api-client/billing-management-service';

import type {
  CampaignProgress
} from '@repo/api-client/billing-management-service';

import
  ScrollReveal
from '@/app/ui/scroll-reveal';

import
  AnimatedProgressBar
from '@/app/ui/campaign/animated-progress-bar';

import
  FeaturedCampaignSkeleton
from '@/app/ui/campaign/featured-campaign-skeleton';

// Fetches client-side (not server-rendered) so the homepage itself can be
// static/cacheable - this section used to force the whole page dynamic
// (export const dynamic = 'force-dynamic') purely so this one live BMS
// fetch happened per-request instead of at build time. Client-side fetch
// solves the same problem (always fresh, never baked into a static build)
// without paying for it on every other section of the page - see
// Brain/20-wiki/tech/platform-architecture.md for the original constraint.
export default function FeaturedCampaignSection() {

  const campaignKey = process.env.NEXT_PUBLIC_FEATURED_CAMPAIGN_KEY;
  const [ progress, setProgress ] = useState<CampaignProgress | null>(null);
  // Distinct from `progress === null`, which also covers "resolved, nothing
  // to show" (no key, fetch failed, campaign closed) - without this, there's
  // no way to tell "still loading" from "permanently nothing here", so the
  // section either has to skip a loading state or flash one forever on a
  // closed/missing campaign.
  const [ loading, setLoading ] = useState(!!process.env.NEXT_PUBLIC_FEATURED_CAMPAIGN_KEY);

  useEffect(() => {

    if (!campaignKey) {

      return;
    }

    let cancelled = false;

    getCampaignByKey(campaignKey)
      .then((res) => {

        if (!cancelled) {

          setProgress(res.data);
        }
      })
      .catch(() => {
        // Silently stay hidden - matches the original server-side behavior
        // of returning null on any fetch failure.
      })
      .finally(() => {

        if (!cancelled) {

          setLoading(false);
        }
      });

    return () => {

      cancelled = true;
    };
  }, [ campaignKey ]);

  if (!campaignKey) {

    return null;
  }

  if (loading) {

    return <FeaturedCampaignSkeleton />;
  }

  if (!progress) {

    return null;
  }

  const {
    campaign, raisedCents
  } = progress;

  if (campaign.campaignStatus === 'CLOSED') {

    return null;
  }

  const percent = Math.min(100, Math.round((raisedCents / campaign.goalAmountCents) * 100));

  return (
    <SectionLayout
      label="Active Campaign"
      heading={campaign.campaignName}
      description={campaign.description ?? undefined}
      maxWidth="2xl"
    >
      <ScrollReveal className="space-y-6">
        <div>
          <div className="flex items-baseline justify-between text-sm mb-2">
            <span className="font-semibold text-[#111111] dark:text-white">
              R
              {(raisedCents / 100).toLocaleString('en-ZA', {
                minimumFractionDigits: 2
              })}
              {' '}
              raised
            </span>
            <span className="text-gray-400">
              of R
              {(campaign.goalAmountCents / 100).toLocaleString('en-ZA', {
                minimumFractionDigits: 2
              })}
              {' '}
              goal
            </span>
          </div>
          <AnimatedProgressBar percent={percent} />
        </div>

        <div className="text-center">
          <PrimaryButton href={`/donate/${ campaignKey }`} className="px-8 py-3">
            Support This Campaign
          </PrimaryButton>
        </div>
      </ScrollReveal>
    </SectionLayout>
  );
}
